import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Modal } from "./Modal";

interface ConfirmOpts {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
}
interface PromptOpts {
  title: string;
  body?: ReactNode;
  label?: string;
  initial?: string;
  placeholder?: string;
  confirmLabel?: string;
  multiline?: boolean;
  inputType?: string;
}

interface Toast {
  id: number;
  kind: "error" | "info";
  message: string;
}

interface DialogApi {
  confirm: (o: ConfirmOpts) => Promise<boolean>;
  prompt: (o: PromptOpts) => Promise<string | null>;
  toast: (message: string, kind?: Toast["kind"]) => void;
}

const Ctx = createContext<DialogApi | null>(null);

// Exposed for non-React callers (e.g. the global react-query MutationCache).
export const globalToast: { current: DialogApi["toast"] | null } = { current: null };

type Pending =
  | { type: "confirm"; opts: ConfirmOpts; resolve: (v: boolean) => void }
  | { type: "prompt"; opts: PromptOpts; resolve: (v: string | null) => void };

export function DialogProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const [value, setValue] = useState("");
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const confirm = useCallback(
    (opts: ConfirmOpts) =>
      new Promise<boolean>((resolve) => setPending({ type: "confirm", opts, resolve })),
    [],
  );
  const prompt = useCallback(
    (opts: PromptOpts) =>
      new Promise<string | null>((resolve) => {
        setValue(opts.initial ?? "");
        setPending({ type: "prompt", opts, resolve });
      }),
    [],
  );
  const toast = useCallback((message: string, kind: Toast["kind"] = "error") => {
    const id = nextId.current++;
    setToasts((t) => [...t, { id, kind, message }]);
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 6000);
  }, []);

  const api = useMemo(() => ({ confirm, prompt, toast }), [confirm, prompt, toast]);
  globalToast.current = toast;

  const close = (result: boolean) => {
    if (!pending) return;
    if (pending.type === "confirm") pending.resolve(result);
    else pending.resolve(result ? value : null);
    setPending(null);
  };

  return (
    <Ctx.Provider value={api}>
      {children}
      {pending && (
        <Modal
          title={pending.opts.title}
          onClose={() => close(false)}
          width={460}
          footer={
            <>
              <button className="btn" onClick={() => close(false)}>
                Cancel
              </button>
              <button
                className={
                  pending.type === "confirm" && pending.opts.danger ? "btn btn-danger" : "btn btn-primary"
                }
                onClick={() => close(true)}
              >
                {pending.opts.confirmLabel ?? (pending.type === "confirm" ? "Confirm" : "OK")}
              </button>
            </>
          }
        >
          {pending.opts.body && <div className="dialog-body">{pending.opts.body}</div>}
          {pending.type === "prompt" && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                close(true);
              }}
            >
              <label className="field">
                {pending.opts.label && <span className="field-label">{pending.opts.label}</span>}
                {pending.opts.multiline ? (
                  <textarea
                    className="input"
                    rows={4}
                    value={value}
                    placeholder={pending.opts.placeholder}
                    onChange={(e) => setValue(e.target.value)}
                  />
                ) : (
                  <input
                    className="input"
                    type={pending.opts.inputType ?? "text"}
                    value={value}
                    placeholder={pending.opts.placeholder}
                    onChange={(e) => setValue(e.target.value)}
                  />
                )}
              </label>
            </form>
          )}
        </Modal>
      )}
      <div className="toasts" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.kind}`}>
            <span>{t.message}</span>
            <button
              className="btn-icon"
              aria-label="Dismiss"
              onClick={() => setToasts((all) => all.filter((x) => x.id !== t.id))}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useDialogs(): DialogApi {
  const c = useContext(Ctx);
  if (!c) throw new Error("useDialogs outside DialogProvider");
  return c;
}
